import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageCircle, ArrowRight, Brain, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackFormSubmission } from '../utils/analytics';

const ThankYouPage = () => {
  const navigate = useNavigate();

  // Fire Google Ads Conversion on page load
  useEffect(() => {
    // Google Ads Conversion Tracking
    if (typeof window !== 'undefined' && window.gtag) {
      // Fire the conversion event
      window.gtag('event', 'conversion', {
        'send_to': 'AW-16923256205/thank_you_page',
        'value': 50.0,
        'currency': 'USD',
        'transaction_id': `lead_${Date.now()}`
      });
      
      // Also fire GA4 event
      window.gtag('event', 'generate_lead', {
        'currency': 'USD',
        'value': 50.0
      });
      
      console.log('📊 Thank You Page Conversion Fired');
    }

    // Track form submission completion
    trackFormSubmission('thank_you_page_view', {
      page: 'thank-you',
      timestamp: new Date().toISOString()
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950/30 to-black flex items-center justify-center px-6">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-violet-600/10 via-transparent to-transparent"></div>
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>

      <div className="relative z-10 max-w-2xl w-full text-center">
        {/* Success Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-2xl shadow-green-500/30 animate-bounce">
            <CheckCircle2 className="w-14 h-14 text-white" />
          </div>
        </div>

        {/* Main Message */}
        <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
          Your Seat Request Has Been{' '}
          <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Received!</span>
        </h1>

        <p className="text-xl text-gray-300 mb-8">
          Thank you for your interest in AI School for All.
        </p>

        {/* Next Steps Box */}
        <div className="bg-gray-900/50 backdrop-blur rounded-3xl p-8 border border-gray-800 mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Clock className="w-6 h-6 text-violet-400" />
            <h2 className="text-xl font-bold text-white">What Happens Next?</h2>
          </div>
          
          <div className="space-y-4 text-left max-w-md mx-auto">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-violet-400 font-bold text-sm">1</span>
              </div>
              <p className="text-gray-300">Our team will review your application</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-violet-400 font-bold text-sm">2</span>
              </div>
              <p className="text-gray-300">You'll receive a call within <span className="text-white font-semibold">24 hours</span></p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-violet-400 font-bold text-sm">3</span>
              </div>
              <p className="text-gray-300">We'll help you complete your enrollment</p>
            </div>
          </div>
        </div>

        {/* Quick Connect */}
        <p className="text-gray-400 mb-4">Need immediate assistance?</p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://wa.me/919891550666?text=Hi, I just submitted a form on AISchoolForAll. I'd like to know more about the courses."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thankyou-whatsapp"
            className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-bold transition-all hover:scale-105"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp Us Now
          </a>
        </div>

        {/* Back to Home */}
        <div className="mt-12">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white px-6 py-3 rounded-full"
          >
            Back to Home <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Branding */}
        <div className="mt-12 flex items-center justify-center gap-2 text-gray-500">
          <Brain className="w-5 h-5 text-violet-500" />
          <span className="font-semibold">AI School for All</span>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
